"use client";

import { useState } from "react";
import Link from "next/link";
import { type BlogCategory } from "@/lib/blog-config";
import ArticleCard, { type BlogArticle } from "./ArticleCard";

type Props = {
    initialOffset: number;
    category: BlogCategory;
    pageSize: number;
};

/**
 * Client-side "Load more" button for /blog. Tracks the next offset
 * locally and GETs /api/blog/list to fetch the next page of articles,
 * then appends them to a grid below the server-rendered one.
 *
 * Behaviour (identical to the pre-redesign LoadMoreButton — only the
 * presentation changed):
 *   - With JS: click → preventDefault → fetch → append (SPA-like, URL
 *     does NOT change). This is the path everyone takes in practice.
 *   - Soft cap at MAX_CLIENT_BATCHES batches loaded. Past the cap the
 *     button's click handler stops calling preventDefault and the
 *     <Link> navigation kicks in — the next batch loads as a fresh
 *     server-rendered page at /blog?offset=N. This keeps DOM size
 *     bounded on very long sessions (without the cap, scrolling
 *     forever would balloon memory) AND gives search engines a real
 *     URL per "page" to crawl.
 *   - Without JS / pre-hydration: the click is a normal link click,
 *     navigates to /blog?offset=N, server renders that batch.
 */
const MAX_CLIENT_BATCHES = 6; // 6 client batches × 9 = 54 appended + 9 SSR ≈ 60 total

export default function LoadMore({ initialOffset, category, pageSize }: Props) {
    const [offset, setOffset] = useState(initialOffset);
    const [items, setItems] = useState<BlogArticle[]>([]);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // batchesAppended counts ONLY the batches loaded via JS append.
    // Once it reaches MAX_CLIENT_BATCHES, the next click should let the
    // native <Link> handle navigation so the browser ships fresh HTML
    // for the next chunk instead of growing the DOM further.
    const batchesAppended = Math.floor(items.length / pageSize);
    const atClientCap = batchesAppended >= MAX_CLIENT_BATCHES;

    async function loadMore(e: React.MouseEvent) {
        // At the cap, do NOT preventDefault — let the <Link href> take over
        // and navigate to the next server-rendered page.
        if (atClientCap) return;
        e.preventDefault();
        if (loading || done) return;
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                offset: String(offset),
                limit: String(pageSize),
            });
            if (category && category !== "All") params.set("category", category);
            const res = await fetch(`/api/blog/list?${params}`, { cache: "no-store" });
            if (!res.ok) throw new Error(`Server returned ${res.status}`);
            const data = (await res.json()) as { articles: BlogArticle[]; hasMore?: boolean };
            const next = data.articles ?? [];
            setItems((prev) => [...prev, ...next]);
            // Advance by pageSize, not next.length: the server-side offset
            // is an index into the unfiltered articles table, so the
            // client-tracked offset has to match what the server expects
            // for the next .range() call. Using next.length drifted from
            // the server offset whenever category filtering trimmed the
            // returned batch below pageSize, causing repeated/empty
            // fetches on subsequent clicks.
            setOffset((prev) => prev + pageSize);
            // The API returns hasMore explicitly (via fetch limit+1 probe)
            // so the button hides correctly even when the final page returns
            // exactly pageSize rows. Falls back to the old length sniff if
            // the response is from a cached/older build.
            if (typeof data.hasMore === "boolean") {
                if (!data.hasMore) setDone(true);
            } else if (next.length < pageSize) {
                setDone(true);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load more articles");
        } finally {
            setLoading(false);
        }
    }

    return (
        // One block-level wrapper so we render as a single child of the
        // parent flow — the appended grid and the button row stack
        // vertically at full width below the SSR grid.
        <div style={{ width: "100%" }}>
            {items.length > 0 && (
                <div className="bl-grid bl-grid-appended">
                    {items.map((article) => (
                        <ArticleCard key={article.id} article={article} />
                    ))}
                </div>
            )}

            {!done && (
                <div className="bl-loadmore-row">
                    <Link
                        href={`/blog?category=${encodeURIComponent(category)}&offset=${offset}`}
                        onClick={loadMore}
                        aria-busy={loading}
                        className="bl-loadmore"
                    >
                        {loading ? "Loading more…" : "Load more articles"}
                    </Link>
                </div>
            )}

            {error && (
                <div role="alert" className="bl-loadmore-error">
                    {error}. Try again.
                </div>
            )}
        </div>
    );
}
